import { useCallback, useEffect, useMemo, useState } from 'react';

import type { RefetchSupportedType } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { approvalActivityHistoryQuery } from './ApprovalActivityHistoryQuery.ts';
import type {
	GraphQLContentStatus,
	ApprovalActivityHistoryQuery$data,
	ApprovalActivityHistoryQuery$variables,
} from './__generated__/ApprovalActivityHistoryQuery.graphql.ts';
import {
	mapApprovalActivityHistory,
	mergeApprovalActivityHistoryQueryData,
} from './activityMappers.ts';
import type { ApprovalActivityItem, ApprovalActivityPageInfo } from './approvalActivityTypes.ts';

export const APPROVAL_ACTIVITY_HISTORY_PAGE_SIZE = 20;
const CURRENT_CONTENT_STATUS: GraphQLContentStatus = 'CURRENT';

export type UseApprovalActivityHistoryOptions = {
	cloudId?: string | null;
	contentId?: string | null;
	contentStatus?: GraphQLContentStatus;
	first?: number;
	skip?: boolean;
};

export function useApprovalActivityHistory({
	cloudId,
	contentId,
	contentStatus = CURRENT_CONTENT_STATUS,
	first = APPROVAL_ACTIVITY_HISTORY_PAGE_SIZE,
	skip = false,
}: UseApprovalActivityHistoryOptions): {
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	loadNextPage: () => Promise<void>;
	loadNextPageError: Error | null;
	refetch: RefetchSupportedType<
		ApprovalActivityHistoryQuery$data,
		ApprovalActivityHistoryQuery$variables
	>;
	items: ApprovalActivityItem[];
	pageInfo: ApprovalActivityPageInfo;
} {
	// Inline rollout check for the approvals EAP experiment.
	const shouldSkip =
		skip || !cloudId || !contentId || !expValEquals('confluence_approvals_eap', 'isEnabled', true);

	const variables = useMemo<ApprovalActivityHistoryQuery$variables>(
		() => ({
			cloudId: cloudId ?? '',
			contentId: contentId ?? '',
			first,
			after: null,
			contentStatuses: [contentStatus],
		}),
		[cloudId, contentId, contentStatus, first],
	);

	const { data, loading, error, fetchMore, refetch } = useQueryInterop<
		ApprovalActivityHistoryQuery$data,
		ApprovalActivityHistoryQuery$variables
	>(approvalActivityHistoryQuery, {
		variables,
		skip: shouldSkip,
		fetchPolicy: 'cache-and-network',
		notifyOnNetworkStatusChange: true,
	});

	const history = useMemo(() => mapApprovalActivityHistory(data), [data]);

	// Mark surfaced errors as handled to avoid duplicate reporting from the global
	// boundary; the error is still returned to callers for rendering.
	useEffect(() => {
		if (error) {
			markErrorAsHandled(error);
		}
	}, [error]);

	// Surfaced separately from the initial-load `error` so a failed page fetch
	// can show an inline retry without replacing the already-loaded list.
	const [loadNextPageError, setLoadNextPageError] = useState<Error | null>(null);

	const loadNextPage = useCallback(async (): Promise<void> => {
		const endCursor = history.pageInfo.endCursor;
		if (!history.pageInfo.hasNextPage || !endCursor || shouldSkip) {
			return;
		}

		setLoadNextPageError(null);
		try {
			await fetchMore({
				variables: {
					...variables,
					after: endCursor,
				},
				updateQuery: (previousResult, { fetchMoreResult }) =>
					mergeApprovalActivityHistoryQueryData(previousResult, fetchMoreResult),
			});
		} catch (fetchMoreError) {
			// Mark handled (fire-and-forget callers shouldn't trigger unhandled
			// rejections) but still surface it so the panel can offer a retry.
			markErrorAsHandled(fetchMoreError);
			setLoadNextPageError(
				fetchMoreError instanceof Error
					? fetchMoreError
					: new Error('Failed to load more activity'),
			);
		}
	}, [fetchMore, history.pageInfo.endCursor, history.pageInfo.hasNextPage, shouldSkip, variables]);

	return {
		...history,
		loading,
		error,
		loadNextPage,
		loadNextPageError,
		refetch,
	};
}
